import React from 'react';

/**
 * OptimizedPicture - Componente para imágenes optimizadas con múltiples formatos
 * 
 * Soporta WebP y AVIF con fallback a JPG/PNG.
 * Reduce el peso de imágenes en 70% comparado con JPG tradicional.
 * 
 * Uso:
 * <OptimizedPicture
 *   srcJpg={product.image}
 *   srcWebp={product.imageWebp}  // Opcional
 *   srcAvif={product.imageAvif}   // Opcional
 *   alt={product.name}
 *   className="img-fluid imgProduct"
 *   loading="lazy"
 * />
 * 
 * @param {string} srcJpg - URL de la imagen JPG (fallback obligatorio)
 * @param {string} srcWebp - URL de la imagen WebP (opcional)
 * @param {string} srcAvif - URL de la imagen AVIF (opcional, mejor compresión)
 * @param {string} alt - Texto alternativo
 * @param {string} className - Clases CSS
 * @param {string} loading - "lazy" | "eager"
 * @param {string} decoding - "async" | "sync"
 * @param {object} style - Estilos inline
 */
const OptimizedPicture = ({
  srcJpg,
  srcWebp,
  srcAvif,
  alt,
  className = '',
  loading = 'lazy',
  decoding = 'async',
  style = {},
  ...props
}) => {
  // Handler para errores de carga de imagen
  const handleImageError = (e) => {
    // Si la imagen falla al cargar (CORS, 404, etc.), ocultarla silenciosamente
    e.target.style.display = 'none';
  };

  // Si no hay formatos modernos, usar img simple
  if (!srcWebp && !srcAvif) {
    return (
      <img
        src={srcJpg}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        style={style}
        onError={handleImageError}
        {...props}
      />
    );
  }

  // Usar <picture> con múltiples formatos
  return (
    <picture className="carta-product-picture">
      {/* AVIF - Mejor compresión (soporte limitado) */}
      {srcAvif && (
        <source
          srcSet={srcAvif}
          type="image/avif"
        />
      )}
      
      {/* WebP - Buena compresión, amplio soporte */}
      {srcWebp && (
        <source
          srcSet={srcWebp}
          type="image/webp"
        />
      )}
      
      {/* Fallback JPG/PNG */}
      <img
        src={srcJpg}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        style={style}
        onError={handleImageError}
        {...props}
      />
    </picture>
  );
};

export default OptimizedPicture;
